import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendNotifications } from '../../../../apis/sendNotification';
import ALIGN_RIGHT_ICON from '../../../../assets/svg/align-right-icon.svg';
import CreditCardIcon from '../../../../assets/svg/credit-card-icon.svg';
import ELLIPSE from '../../../../assets/svg/ellipse.svg';
import GreenClock from '../../../../assets/svg/green-clock.svg';
import SeatIcon from '../../../../assets/svg/seat-icon.svg';
import WalletIcon from '../../../../assets/svg/wallet-icon.svg';
import YellowClock from '../../../../assets/svg/yellow-clock.svg';
import Button from '../../../../components/Button/Button';
import PrintButton from '../../../../components/PrintButton/PrintButton';
import { LANG_TEXT } from '../../../../constants/language';
import { DELIVERY_FEE, ROLES } from '../../../../consts/language';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { useMenuContext } from '../../../../context/MenuContext';
import usePermission from '../../../../hooks/usePermission';
import { usePreorderEnabled } from '../../../../hooks/usePreorderEnabled';
import { buttonColors, colors } from '../../../../interfaces/color.types';
import { DamagedItemStatus, OrderItemStatus, PaymentMethod } from '../../../../interfaces/index.types';
import { updateOrderDoc } from '../../../../apis/updateOrderDoc';
import { doc, getDoc } from '../../../../libs/firestore-wrapper';
import { getAllOrders } from '../../../../modals/order';
import { formatPriceItem } from '../../../../utils';
import { formatHourMonthDayYear } from '../../../../utils/handleDateTime';
import { isAllMatch } from '../../../../utils/multiOrder.utils';
import { IFood, IMultiOrder, IOrders } from '../../order.types';
import ConfirmDamaged from '../ConfirmDamaged/ConfirmDamaged';
import FoodItem from '../FoodItem/FoodItem';
import OrderCancellation from '../OrderCancellation/OrderCancellation';
import PartialCancellation from '../PartialCancellation/PartialCancellation';
import { buttonPrintStyle, buttonStyle, textButton, useStyles } from './styles';

function DrawerOrderItem({
  order,
  toggleDrawer,
  selectedFoodId,
  selectedRestaurantId,
}: {
  order?: IOrders;
  toggleDrawer?: (open: boolean) => void;
  selectedFoodId?: string;
  selectedRestaurantId?: string;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { foodMenu } = useMenuContext();
  const [cancellationOrder, setCancellationOrder] = useState<boolean>(false);
  const [partialCancellation, setPartialCancellation] = useState<boolean>(false);
  const { userInfo } = useAuthContext();
  const { restaurantId: permissionRestaurantId } = usePermission();

  const { enabledNewVersion } = usePreorderEnabled();

  const restaurantId = selectedRestaurantId || permissionRestaurantId;

  const updateOrderStatus = async (orderId: string, status: string, updatedFoods?: IOrders['foods'], allowRegression?: boolean) => {
    const orderRef = doc(getAllOrders(), orderId);
    const _order: IOrders = (await getDoc(orderRef)).data();

    const existingMultiOrders = _order?.multi_orders || [];
    const currentRestaurantIndex = existingMultiOrders.findIndex((item) => item.restaurant_id === restaurantId);

    let updatedMultiOrders;
    if (currentRestaurantIndex >= 0) {
      updatedMultiOrders = existingMultiOrders.map((item) => (item.restaurant_id === restaurantId ? { ...item, status } : item));
    } else {
      updatedMultiOrders = [...existingMultiOrders, { restaurant_id: restaurantId, status }];
    }

    const nonCancelledOrders = updatedMultiOrders.filter((item) => item.status !== OrderItemStatus.CANCELLED && item.status !== DamagedItemStatus.DAMAGED_CANCELLED);

    let finalOrderStatus = _order.order_status;

    if (nonCancelledOrders.length === 0) {
      finalOrderStatus = status.includes('DAMAGED') ? DamagedItemStatus.DAMAGED_CANCELLED : OrderItemStatus.CANCELLED;
    } else if (isAllMatch(nonCancelledOrders as IMultiOrder[], status as OrderItemStatus)) {
      finalOrderStatus = status;
    }

    if (enabledNewVersion) {
      const updateData: any = {
        multi_orders: updatedMultiOrders,
      };
      if (updatedFoods) {
        updateData.foods = updatedFoods;
      }
      if (finalOrderStatus !== _order.order_status) {
        updateData.order_status = finalOrderStatus;
      }

      await updateOrderDoc(orderId, updateData, allowRegression);
    } else {
      await updateOrderDoc(
        orderId,
        {
          order_status: status,
          foods: updatedFoods,
        },
        allowRegression
      );
    }
  };

  const [foodQuantities, setFoodQuantities] = useState<Record<string, number>>({});

  const filteredItems = useMemo(() => {
    const items = order?.type === 'promotion' ? order?.promotion_foods || [] : order?.foods || [];
    return selectedRestaurantId ? items.filter((item) => item.restaurantId === selectedRestaurantId) : items;
  }, [order?.type, order?.promotion_foods, order?.foods, selectedRestaurantId]);

  useEffect(() => {
    if (filteredItems.length > 0) {
      const initialQuantities = filteredItems.reduce(
        (acc, food) => {
          acc[food.item_food_id] = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
          return acc;
        },
        {} as Record<string, number>
      );
      setFoodQuantities(initialQuantities);
    }
  }, [filteredItems, cancellationOrder, partialCancellation]);

  const handleQuantityChange = (foodId: string, newQuantity: number) => {
    setFoodQuantities((prev) => ({
      ...prev,
      [foodId]: newQuantity,
    }));
  };

  const updatePromotionOrderStatus = async (orderId: string, status: string) => {
    await updateOrderDoc(orderId, {
      food_promotion_order_status: status,
    });
  };

  const updateRegularOrderStatus = async (orderId: string, status: string) => {
    await updateOrderDoc(orderId, {
      food_order_status: status,
    });
  };

  const handleChangeOrderStatus = async (order: IOrders, currentOrderStatus: OrderItemStatus) => {
    if (currentOrderStatus !== OrderItemStatus.PREPARING && currentOrderStatus !== OrderItemStatus.DAMAGED_PREPARING) {
      return;
    }

    const newStatus = currentOrderStatus === OrderItemStatus.DAMAGED_PREPARING ? OrderItemStatus.DAMAGED_READY_FOR_PICK_UP : OrderItemStatus.READY_FOR_PICK_UP;
    const is_damaged = order?.is_damaged;
    const parent_id = order?.parent_id ?? order.id;

    try {
      if (order.promotion_foods && order.promotion_foods.length > 0) {
        const isMixedStatus =
          (order.food_order_status === OrderItemStatus.READY_FOR_PICK_UP && order.food_promotion_order_status === OrderItemStatus.PREPARING) ||
          (order.food_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP && order.food_promotion_order_status === OrderItemStatus.DAMAGED_PREPARING) ||
          (order.food_order_status === OrderItemStatus.PREPARING && order.food_promotion_order_status === OrderItemStatus.READY_FOR_PICK_UP) ||
          (order.food_order_status === OrderItemStatus.DAMAGED_PREPARING && order.food_promotion_order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP);

        if (isMixedStatus) {
          await updateOrderStatus(order.id, newStatus);
          if (is_damaged) {
            await updateOrderStatus(parent_id, newStatus);
          }
        }

        if (order.type === 'promotion') {
          await updatePromotionOrderStatus(order.id, newStatus);
        } else {
          await updateRegularOrderStatus(order.id, newStatus);
        }
      } else {
        await updateOrderStatus(order.id, newStatus);
        if (is_damaged) {
          await updateOrderStatus(parent_id, newStatus);
        }
      }

      if (order.rider_id) {
        await sendNotifications(order.rider_id, {
          id: parent_id,
          status: newStatus,
        });
      }
      toggleDrawer!(false);
      // navigate(`/dashboard`);
    } catch (error) {
      console.error('Update order status failed:', error);
    }
  };

  const getDeliveryFee = () => {
    return order?.type === 'promotion' || orderStatus === OrderItemStatus.CANCELLED || enabledNewVersion ? 0 : DELIVERY_FEE;
  };

  const orderStatus = useMemo(() => {
    if (!order) return OrderItemStatus.READY_FOR_PICK_UP;

    if (enabledNewVersion) {
      const restaurantOrder = order.multi_orders?.find((item) => item.restaurant_id === restaurantId);
      if (restaurantOrder) {
        return restaurantOrder.status;
      }
    }

    const hasPromotionFoods = order.promotion_foods && order.promotion_foods?.length > 0;
    const currentStatus = enabledNewVersion ? order.multi_orders?.find((item) => item.restaurant_id === restaurantId)?.status || order.order_status : order.order_status;
    const isReadyForPickup = currentStatus === OrderItemStatus.READY_FOR_PICK_UP || currentStatus === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP;

    if (hasPromotionFoods && !(isReadyForPickup && order.rider_id)) {
      return order.type === 'promotion' ? order.food_promotion_order_status : order.food_order_status;
    }

    return currentStatus;
  }, [order, enabledNewVersion, restaurantId]);

  const [price, totalPrice] = useMemo(() => {
    if (!order) return [0, 0];
    const price = filteredItems.reduce((acc, item) => {
      const itemPrice =
        item.item_deleted_quantity !== undefined
          ? parseFloat(((item.item_quantity - item.item_deleted_quantity) * item.item_price).toFixed(2).toString())
          : parseFloat(item.item_subtotal.toString());
      return acc + itemPrice;
    }, 0);
    const deliveryFee = order?.type === 'promotion' || orderStatus === OrderItemStatus.CANCELLED || enabledNewVersion ? 0 : DELIVERY_FEE;
    return [price, price + deliveryFee];
  }, [filteredItems, orderStatus, order]);

  const totalItems = useMemo(() => {
    return filteredItems.reduce(
      (sum, item) => sum + (item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity || 0),
      0
    );
  }, [filteredItems]);

  const isDamagedHighlighted =
    order?.has_damaged_order &&
    [OrderItemStatus.DAMAGED_READY_FOR_PICK_UP, OrderItemStatus.DAMAGED_DELIVERING, OrderItemStatus.DAMAGED_PREPARING].includes(orderStatus as OrderItemStatus);

  const isPreOrder = Boolean(order?.is_pre_order);
  const deliveryRequestValue = order?.delivery_request || 'WHEN_ITS_READY';
  const isWhenItsReady = deliveryRequestValue === 'WHEN_ITS_READY';
  const deliveryClockIcon = isWhenItsReady ? GreenClock : YellowClock;
  const deliveryTextColor = isWhenItsReady ? 'var(--Success-600, #3D9F4A)' : 'var(--Warning-600, #FDB022)';
  const deliveryRequestDisplay = isWhenItsReady ? t(deliveryRequestValue) : deliveryRequestValue;
  const preOrderDeliveryDisplay =
    isWhenItsReady || !order?.requested_delivery_time
      ? t(deliveryRequestValue)
      : deliveryRequestValue || formatHourMonthDayYear(order.requested_delivery_time).time;

  const renderOrderActions = () => {
    if (order) {
      const cancelable =
        userInfo?.role === ROLES.SUPER_ADMIN
          ? orderStatus !== OrderItemStatus.CANCELLED && orderStatus !== DamagedItemStatus.DAMAGED_CANCELLED
          : orderStatus === OrderItemStatus.PREPARING || orderStatus === DamagedItemStatus.DAMAGED_PREPARING;

      if (orderStatus === DamagedItemStatus.DAMAGED_CREATED) {
        return (
          <ConfirmDamaged
            foodQuantities={foodQuantities}
            toggleDrawer={toggleDrawer}
            updatePromotionOrderStatus={updatePromotionOrderStatus}
            updateRegularOrderStatus={updateRegularOrderStatus}
            updateOrderStatus={updateOrderStatus}
            order={order}
            restaurantId={restaurantId}
          />
        );
      }

      if (cancellationOrder && cancelable) {
        return (
          <OrderCancellation
            setCancellationOrder={setCancellationOrder}
            setPartialCancellation={setPartialCancellation}
            toggleDrawer={toggleDrawer}
            updatePromotionOrderStatus={updatePromotionOrderStatus}
            updateRegularOrderStatus={updateRegularOrderStatus}
            updateOrderStatus={updateOrderStatus}
            order={order}
            restaurantId={restaurantId}
          />
        );
      }
      if (partialCancellation && cancelable) {
        return (
          <PartialCancellation
            setCancellationOrder={setCancellationOrder}
            setPartialCancellation={setPartialCancellation}
            toggleDrawer={toggleDrawer}
            updatePromotionOrderStatus={updatePromotionOrderStatus}
            updateRegularOrderStatus={updateRegularOrderStatus}
            updateOrderStatus={updateOrderStatus}
            order={order}
            foodQuantities={foodQuantities}
            restaurantId={restaurantId}
          />
        );
      }

      return (
        <Box className={classes.BoxButton}>
          <Button
            id="BUTTON_CARD"
            fullWidth={false}
            title={t(isDamagedHighlighted ? OrderItemStatus.DELIVERING : buttonColors.find((color) => color.status === orderStatus)?.name || 'none')}
            buttonStyle={{
              ...buttonStyle,
              background: isDamagedHighlighted ? 'var(--Base-White, #FFF) !important' : buttonColors.find((color) => color.status === orderStatus)?.bgrColor || 'none',
            }}
            sxTextStyle={{
              ...textButton,
              color: isDamagedHighlighted ? 'var(--Gray-300, #D0D5DD) !important' : buttonColors.find((color) => color.status === orderStatus)?.color || 'none',
            }}
            disable={!(orderStatus === OrderItemStatus.PREPARING || (order.is_damaged && orderStatus === DamagedItemStatus.DAMAGED_PREPARING))}
            startIcon={
              isDamagedHighlighted || buttonColors.find((color) => color.status === orderStatus)?.icons ? (
                <AccessTimeIcon sx={{ color: 'rgba(208, 213, 221, 1)' }} />
              ) : null
            }
            onClick={() => handleChangeOrderStatus(order, orderStatus as OrderItemStatus)}
          />

          {cancelable && (
            <Button
              id="BUTTON_ORDER_CANCELLATION"
              fullWidth={false}
              title={t(LANG_TEXT.ORDER_CANCELLATION)}
              buttonStyle={{
                ...buttonStyle,
                background: 'var(--Primary-50, #F2FBF3) !important',
              }}
              sxTextStyle={{
                ...textButton,
                color: 'var(--Primary-700, #296C32) !important',
              }}
              onClick={() => {
                setCancellationOrder(true);
              }}
            />
          )}
        </Box>
      );
    }
  };

  useEffect(() => {
    if (selectedFoodId) {
      const el = document.getElementById(`food-${selectedFoodId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedFoodId]);

  return (
    <>
      {order && (
        <Box className={classes.Box} role="presentation">
          <Box className={classes.BoxHeader}>
            <Box className={classes.LeftHeaderBox}>
              <Box
                className={classes.BoxStatus}
                sx={{
                  background: isDamagedHighlighted ? 'var(--Blue-light-50, #F0F9FF)' : colors.find((color) => color.status === orderStatus)?.bgrColor || 'none',
                }}
              >
                <Typography
                  sx={{
                    color: isDamagedHighlighted ? 'var(--Blue-light-700, #026AA2)' : colors.find((color) => color.status === orderStatus)?.color || 'none',
                  }}
                  fontSize={14}
                  fontWeight={500}
                  lineHeight={'20px'}
                  textAlign={'center'}
                >
                  {t(isDamagedHighlighted ? OrderItemStatus.DELIVERING : colors.find((color) => color.status === orderStatus)?.name || 'none')}
                </Typography>
              </Box>
              <Typography fontSize={16} fontWeight={500} lineHeight={'20px'} textAlign={'center'} color={'var(--Gray-800, #1D2939)'}>
                {t(LANG_TEXT.ORDER)} {order.id}
              </Typography>
            </Box>
            <Box className={classes.BoxIcon}>
              <PrintButton data={order} toggleDrawer={toggleDrawer} />
              <Button
                id="close_button"
                fullWidth={false}
                boxStyle={{ width: '100%' }}
                buttonStyle={buttonPrintStyle}
                onClick={() => toggleDrawer!(false)}
                startIcon={<img src={ALIGN_RIGHT_ICON} height={20} width={20} alt="ALIGN_RIGHT_ICON" />}
              />
            </Box>
          </Box>
          <Box className={classes.BoxContent}>
            <Box className={classes.BoxOrderSummaryOuter}>
              <Box className={classes.CompactSummaryBox}>
                <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-700, #344054)'}>
                  {t(LANG_TEXT.ORDER_TOTAL)}
                </Typography>

                <Box className={classes.SummaryRow}>
                  <Box className={classes.ItemsPriceGroup}>
                    <Typography fontSize={32} fontWeight={700} lineHeight={'40px'} color={'var(--Gray-900, #101828)'}>
                      {totalItems} {t(LANG_TEXT.ITEMS)}
                    </Typography>
                    <img src={ELLIPSE} height={4} width={4} alt="ELLIPSE" />
                    <Typography fontSize={20} fontWeight={500} lineHeight={'30px'} color={'var(--Gray-700, #344054)'}>
                      ¥{formatPriceItem(price)}
                    </Typography>
                  </Box>
                  {enabledNewVersion && order?.multi_orders && order.multi_orders.length > 0 && (
                    <Box className={classes.MultiOrderBadge}>
                      <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                        {t(LANG_TEXT.NUMBER_OF_STORES)}: <b>{`${order.multi_orders.length}`}</b>
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box className={classes.TimeSeatRow}>
                  <Box className={classes.TimePill}>
                    {!isPreOrder ? (
                      <>
                        <img src={deliveryClockIcon} height={18} width={18} alt="DeliveryClock" />
                        <Typography fontSize={16} fontWeight={600} lineHeight={'24px'} color={deliveryTextColor}>
                          {deliveryRequestDisplay}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <img src={deliveryClockIcon} height={18} width={18} alt="DeliveryClock" />
                        <Typography fontSize={16} fontWeight={600} lineHeight={'24px'} color={deliveryTextColor}>
                          {preOrderDeliveryDisplay}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {order?.seat_number && (
                    <Box className={classes.SeatBox}>
                      <img src={SeatIcon} height={20} width={20} alt="SeatIcon" />
                      <Typography fontSize={16} fontWeight={400} lineHeight={'24px'} color={'var(--Gray-500, #667085)'}>
                        {order.seat_number}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box className={classes.BoxOrderSumary}>
              <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-700, #344054)'}>
                {t(LANG_TEXT.ORDER_SUMMARY)}
              </Typography>
              {filteredItems.map((food: IFood) => (
                <FoodItem
                  key={food.item_food_id}
                  food={food}
                  foodMenu={foodMenu}
                  order={order}
                  quantity={foodQuantities[food.item_food_id]}
                  onQuantityChange={handleQuantityChange}
                  maxItem={food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity}
                  cancellation_order={cancellationOrder}
                  partialCancellation={partialCancellation}
                />
              ))}
            </Box>
            <Box className={classes.BoxPaymentSumary}>
              <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-700, #344054)'}>
                {t(LANG_TEXT.PAYMENT_SUMMARY)}
              </Typography>
              <Box className={classes.BoxContentPaymentSumary}>
                <Box className={classes.BoxInformationForEachPayment}>
                  <Box className={classes.InformationForEachPayment}>
                    <Typography fontSize={16} lineHeight={'24px'} className={classes.TextTitlePaymentDetail}>
                      {t(LANG_TEXT.PRICE)}
                    </Typography>
                    <Typography fontSize={16} lineHeight={'24px'} fontWeight={500} color={'var(--Gray-700, #344054)'}>
                      ¥ {formatPriceItem(price)}
                    </Typography>
                  </Box>
                </Box>
                {order.type !== 'promotion' && !enabledNewVersion && (
                  <Box className={classes.BoxInformationForEachPayment}>
                    <Box className={classes.InformationForEachPayment}>
                      <Typography fontSize={16} lineHeight={'24px'} className={classes.TextTitlePaymentDetail}>
                        {t(LANG_TEXT.SHIPPING_FEE)}
                      </Typography>
                      <Typography fontSize={16} lineHeight={'24px'} fontWeight={500} color={'var(--Gray-700, #344054)'}>
                        ¥ {getDeliveryFee()}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box className={classes.BoxInformationForEachPayment}>
                  <Box className={classes.InformationForEachPayment}>
                    <Typography fontSize={16} lineHeight={'24px'} className={classes.TextTitlePaymentDetail}>
                      {t(LANG_TEXT.PAYMENT_METHOD)}
                    </Typography>
                    <Box className={classes.PaymentMethod}>
                      <img src={order.payment_method === PaymentMethod.Credit ? CreditCardIcon : WalletIcon} alt="wallet" width={20} height={14} />
                      <Typography fontSize={16} lineHeight={'24px'} fontWeight={500} color={'var(--Gray-700, #344054)'}>
                        {order.payment_method === PaymentMethod.Credit ? t(PaymentMethod.Credit) : t(order.payment_method)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.BoxInformationForEachPayment} sx={{ borderTop: '1px solid var(--Gray-300, #D0D5DD)' }}>
                  <Box className={classes.InformationForEachPayment}>
                    <Typography fontSize={20} lineHeight={'30px'} fontWeight={500}>
                      {t(LANG_TEXT.TOTAL_PAYMENT)}
                    </Typography>
                    <Typography fontSize={20} lineHeight={'30px'} fontWeight={500} color={'var(--Gray-700, #1D2939)'}>
                      ¥ {formatPriceItem(totalPrice)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={classes.BoxFooter}>
            <Box className={classes.BoxTextFooter}>
              <Typography fontSize={20} lineHeight={'30px'} fontWeight={600} color={'var(--Gray-500, #667085)'}>
                {totalItems} {t(LANG_TEXT.ITEMS)}
              </Typography>
              <Typography fontSize={24} lineHeight={'32px'} fontWeight={500} color={'var(--Primary-700, #296C32)'}>
                ¥ {formatPriceItem(totalPrice)}
              </Typography>
            </Box>
            {renderOrderActions()}
          </Box>
        </Box>
      )}
    </>
  );
}

export default DrawerOrderItem;
